import { linkTo } from '@99designs/routing';
import type { ProductKey } from '@99designs/marketing-ui';
import type { Product } from './types';

export function getProducts(keys: ProductKey[]): Product[] {
    const products = PRODUCTS();
    return keys.map((key) => products[key]);
}

const PRODUCTS = (): Record<ProductKey, Product> => ({
    'logo-brand-guide': {
        categoryKey: 'logo-brand-guide',
        marketingKey: 'logo-brand-guide',
        productName: 'bronze-logo-brand-guide-contest',
        title: __('Logo & brand guide'),
        description: __(
            'A logo might be the main ingredient. But a brand guide adds the flavor: all the colors and typography that will bring your brand to life in print and online.'
        ),
        deliverables: [
            __('Logo design'),
            __('Brand guide document (fonts, colors, guidelines for usage)'),
        ],
        image: {
            src: 'https://99designs-start-static.imgix.net/product-details/related-products/logo-brand-guide.jpeg',
            alt: '',
            designerAttribution: { text: 'Iris Design', userId: '2778758' },
        },
        url: '/logo-brand-guide/details',
    },
    'brand-guide': {
        categoryKey: 'brand-guide',
        marketingKey: 'brand-guide',
        productName: 'brand-guide-generic',
        title: '',
        description: '',
        deliverables: [],
        image: {
            src: 'https://99designs-start-static.imgix.net/product-details/related-products/brand-guide.jpg',
            alt: '',
        },
        url: '/brand-guide',
    },
    'logo-brand-identity-pack': {
        categoryKey: 'brand-identity-pack',
        marketingKey: 'logo-brand-identity-pack',
        productName: 'bronze-logo-brand-identity-pack-contest',
        title: __('Logo & brand identity pack'),
        description: __(
            "Nail that first impression with a stunning logo, and seal the deal with a flawless brand that's as unforgettable as your business."
        ),
        deliverables: [
            __('Logo design'),
            __('Business card'),
            __('Letterhead & envelope'),
            __('Facebook cover'),
        ],
        image: {
            src: 'https://99designs-start-static.imgix.net/product-details/related-products/brand-identity-pack.jpeg',
            alt: '',
            designerAttribution: { text: 'Satryo', userId: '3100340' },
        },
        url: '/brand-identity-pack/details',
    },
    'logo-business-card-design': {
        categoryKey: 'logo-business-card-design',
        marketingKey: 'logo-business-card-design',
        productName: 'bronze-logo-business-card-contest',
        title: __('Logo & business card'),
        description: __(
            'Get down to business with a custom logo and matching business card design.'
        ),
        deliverables: [__('Logo design'), __('Business card')],
        image: {
            src: 'https://99designs-start-static.imgix.net/product-details/related-products/logo-business-card-design.jpeg',
            alt: '',
            designerAttribution: { text: 'artsigma', userId: '1284917' },
        },
        url: '/logo-business-card-design/details',
    },
    'logo-design': {
        categoryKey: 'logo-design',
        marketingKey: 'logo-design',
        productName: 'bronze-logo-contest',
        title: __('Logo design'),
        description: __(
            'Whether you crave clean minimalism or are gaga for grunge, our professional designers will create the perfect custom logo for your brand.'
        ),
        deliverables: [__('Logo design')],
        image: {
            src: 'https://99designs-start-static.imgix.net/product-details/related-products/logo-design.jpeg',
            alt: '',
            designerAttribution: { text: 'GS | Designs', userId: '977680' },
        },
        url: '/logo-design/details',
    },
    'logo-website-squarespace': {
        categoryKey: 'logo-website-squarespace',
        marketingKey: 'logo-website-squarespace',
        title: __('Logo & website'),
        saving: __('Save up to US$300'),
        description: __(
            'The two must-haves for any brand are available in one nifty bundle. Get a custom logo design with a matching Squarespace website, all in one easy flow.'
        ),
        deliverables: [
            __('Logo design'),
            __('Fully functioning, custom-designed Squarespace website'),
            __('Content uploaded for you'),
        ],
        image: {
            src: 'https://99designs-start-static.imgix.net/product-details/related-products/logo-website-squarespace.jpg',
            alt: '',
        },
        url: '/logo-website-squarespace',
    },
    'logo-social-media-pack': {
        categoryKey: 'social-media-pack',
        marketingKey: 'logo-social-media-pack',
        productName: 'bronze-logo-social-media-pack-contest',
        title: __('Logo & social media pack'),
        description: __(
            'We all wanna be liked (and retweeted). Set your brand up for success with a logo and matching Facebook, Twitter and YouTube images.'
        ),
        deliverables: [
            __('Logo design'),
            __('Facebook cover'),
            __('Twitter header'),
            __('YouTube background'),
        ],
        image: {
            src: 'https://99designs-start-static.imgix.net/product-details/related-products/social-media-pack.jpeg',
            alt: '',
            designerAttribution: { text: 'Suparde', userId: '3004243' },
        },
        url: '/social-media-pack/details',
    },
    'facebook-creative': {
        categoryKey: 'facebook-creative',
        marketingKey: 'facebook-creative',
        productName: 'facebook-intro',
        title: __('Facebook creative'),
        description: __(
            "Facebook has partnered with 99designs to match you with designers vetted for their expertise with Facebook ads. Get custom Facebook ads to ensure you'll stand out online."
        ),
        deliverables: [__('Facebook ad creative')],
        image: {
            src: 'https://99designs-start-static.imgix.net/product-details/related-products/facebook-creative.jpg',
            alt: '',
        },
        url: '/facebook',
    },
    'business-card-design': {
        categoryKey: 'business-card-design',
        marketingKey: 'business-card-design',
        productName: 'business-card-generic',
        title: '',
        description: '',
        deliverables: [],
        image: {
            src: 'https://99designs-start-static.imgix.net/product-details/related-products/business-card-essential.jpg',
            alt: '',
            designerAttribution: { text: 'artsigma', userId: '1284917' },
        },
        url: '/business-card-design',
    },
    'product-packaging-design': {
        categoryKey: 'product-packaging-design',
        marketingKey: 'product-packaging-design',
        productName: 'product-packaging-generic',
        title: '',
        description: '',
        deliverables: [],
        image: {
            src: 'https://99designs-start-static.imgix.net/product-details/related-products/product-packaging-design.jpg',
            alt: '',
        },
        url: '/product-packaging-design',
    },
    'signage-design': {
        categoryKey: 'signage-design',
        marketingKey: 'signage-design',
        productName: 'signage-generic',
        title: '',
        description: '',
        deliverables: [],
        image: {
            src: 'https://99designs-start-static.imgix.net/product-details/related-products/signage-design.jpg',
            alt: '',
        },
        url: '/signage-design',
    },
    'brochure-design': {
        categoryKey: 'brochure-design',
        marketingKey: 'brochure-design',
        productName: 'bronze-brochure-contest',
        title: __('Brochure'),
        description: __(
            "A brochure is your brand at a glance. The perfect brochure gives your audience everything they need to say 'yes' to you."
        ),
        deliverables: [__('Brochure design')],
        image: {
            src: 'https://99designs-start-static.imgix.net/product-details/related-products/brochure-essential.jpg',
            alt: '',
            designerAttribution: { text: 'artsigma', userId: '1284917' },
        },
        url: '/brochure-design',
    },
    'squarespace-web-commerce': {
        categoryKey: 'squarespace-web-commerce',
        marketingKey: 'squarespace-web-commerce',
        productName: 'squarespace-web-commerce',
        title: '',
        description: '',
        deliverables: [],
        image: {
            src: 'https://99designs-start-static.imgix.net/product-details/related-products/squarespace-web-commerce.jpg',
            alt: '',
        },
        url: linkTo.briefCreate('squarespace-web-commerce'),
    },
    'product-label-design': {
        categoryKey: 'product-label-design',
        marketingKey: 'product-label-design',
        productName: 'product-label-generic',
        title: '',
        description: '',
        deliverables: [],
        image: {
            src: 'https://99designs-start-static.imgix.net/product-details/related-products/product-label-design.jpg',
            alt: '',
        },
        url: '/product-label-design',
    },
});
